/*
 * Marketing > SEO Origin & Backlinks tab content.
 *
 * Figures are RESEARCH ESTIMATES / competitor notes (last checked 5 Nov 2026), not a live
 * Ahrefs / Search Console feed. Re-check Search Console, sitemap.xml and a backlink tool
 * and update leader / us notes when numbers change.
 *
 * Task keys (seo-origin-<topic>-<action> and seo-backlink-<topic>-<action>) are stored by
 * the roadmap endpoints. Never rename an id once in use, or its tick and comments will
 * no longer match. Keyword climb tasks stay as seo-<keywordSlug>-<actionSlug>.
 */

export const OUR_SITE = "https://11plusmagic.co.uk/";
export const LAST_CHECKED = "2026-11-05";

export const PILLARS = {
  origin: { id: "origin", label: "SEO Origin (on-site)", color: "#2563eb" },
  backlinks: { id: "backlinks", label: "Backlink SEO", color: "#7c3aed" },
};

export const STATUS = {
  missing: { label: "Missing", color: "#b91c1c" },
  weak: { label: "Weak / partial", color: "#b45309" },
  strong: { label: "In place", color: "#047857" },
};

const a = (id, title, detail, effort) => ({ id, title, detail, ...(effort ? { effort } : {}) });

export const SECTIONS = [
  {
    id: "origin",
    label: PILLARS.origin.label,
    color: PILLARS.origin.color,
    summary: "Organic / SEO channel foundation: Search Console, sitemap, technical SEO, titles, schema and the core landing pages Google needs to see.",
    topics: [
      {
        id: "search-console",
        name: "Google Search Console",
        status: "missing",
        leader: { name: "Atom Learning / EPP", note: "Mature sites with sitemaps submitted and branded + non-branded queries tracked." },
        us: { note: "Verify and sitemap submit are still open tasks on the Keyword Rankings brand keyword.", metric: "Property: not confirmed" },
        gap: "No shared admin view of impressions, clicks, coverage or sitemap status.",
        actions: [
          a("verify", "Verify 11plusmagic.co.uk in Google Search Console", "DNS or HTML-tag verify; add both www and non-www if needed.", "half day"),
          a("sitemap", "Submit /sitemap.xml and check Coverage", "Fix blocked or soft-404 URLs; note index count in a comment.", "half day"),
          a("branded-query", "Baseline branded query impressions for '11 plus magic'", "Screenshot Performance for the last 28 days into comments."),
          a("share-access", "Add the marketing admin Google account as a full user", "So ticks here match what the team can see."),
        ],
      },
      {
        id: "technical",
        name: "Technical SEO (crawlability & Core Web Vitals)",
        status: "weak",
        leader: { name: "Atom Learning", note: "Pre-rendered / fast content pages; large crawlable HTML library." },
        us: { note: "Competitors roadmap already calls out pre-rendering so Google sees text; home CWV is a Keyword Rankings action.", metric: "Indexable marketing pages: ~18 (competitors note)" },
        gap: "SPA / thin crawlable HTML vs Atom's 1,608 sitemap URLs.",
        actions: [
          a("prerender", "Pre-render or SSR key marketing routes", "Home, free test, practice papers, app, tuition, regional guides.", "2-3 days"),
          a("robots", "Publish robots.txt that allows Googlebot and points to the sitemap", "Block only admin / app-auth routes."),
          a("cwv", "Pass mobile Core Web Vitals on home and free-test pages", "PageSpeed Insights; LCP under 2.5s.", "1 day"),
          a("canonicals", "Add self-referencing canonicals on every public page", "Avoid duplicate www / trailing-slash variants."),
        ],
      },
      {
        id: "sitemap-ia",
        name: "Sitemap & information architecture",
        status: "weak",
        leader: { name: "Atom Learning", note: "1,608 URLs: 721 blog posts, 389 free-resource pages, 163 grammar-school pages (see Competitors SEO channel)." },
        us: { note: "About 18 URLs and 0 blog posts per Competitors scorecard.", metric: "Sitemap URLs: ~18 vs Atom 1,608" },
        gap: "About 89x fewer pages; no school / area / subject hub tree.",
        actions: [
          a("xml-sitemap", "Generate a complete XML sitemap of public pages", "Include new Keyword Rankings landing pages as they ship.", "half day"),
          a("hub-tree", "Define the hub tree: subjects, exam boards, regions, free resources", "One spreadsheet linked from a comment; match Atom's buckets at smaller scale."),
          a("nav", "Expose hubs in header / footer nav", "So crawlers and parents can reach them in 2 clicks."),
          a("html-sitemap", "Add a simple HTML sitemap page for parents", "Also helps internal linking."),
        ],
      },
      {
        id: "titles-meta",
        name: "Titles, metas & on-page templates",
        status: "weak",
        leader: { name: "Atom / Eleven Plus Exams", note: "Exact-match titles on practice-paper and free-resource pages." },
        us: { note: "Home copy does not yet target core 'online practice' phrases; per-keyword title work is scattered.", metric: "Shared title template: none" },
        gap: "No shared title/meta pattern for new landing pages.",
        actions: [
          a("template", "Write a title + meta template for landing pages", "Primary keyword near the front; include 'free' or 'GL & CEM' where true.", "half day"),
          a("home-title", "Rewrite the home title/meta for online 11+ practice", "Align with the Keyword Rankings online-practice climb actions."),
          a("og-tags", "Add Open Graph / Twitter cards on core pages", "Correct brand name 11 Plus Magic (not EdTech-Ltd)."),
        ],
      },
      {
        id: "schema",
        name: "Structured data (schema)",
        status: "missing",
        leader: { name: "Publishers / Atom", note: "FAQ and product-style rich results appear on competitor resource pages." },
        us: { note: "Organization / FAQ / Product schema called out on brand and practice-paper keywords but not shipped as a site-wide baseline.", metric: "Rich results: none confirmed" },
        gap: "No Organization, FAQ or Product JSON-LD on the live site.",
        actions: [
          a("org", "Add Organization schema with sameAs social + app-store links", "Matches the brand keyword action; do it once site-wide.", "half day"),
          a("faq", "Add FAQ schema on free-test and tuition pages", "Validate in Rich Results Test."),
          a("product", "Add Product / SoftwareApplication schema on the app page", "Price, platform, free trial where accurate."),
          a("breadcrumbs", "Add BreadcrumbList schema on hub and guide pages", ""),
        ],
      },
      {
        id: "core-pages",
        name: "Core landing pages (indexable)",
        status: "missing",
        leader: { name: "Atom Learning", note: "Dedicated practice-papers, free resources, school and area pages rank for commercial queries." },
        us: { note: "Keyword Rankings gaps: no practice-papers, free-test, mocks, tuition, regional or app landing pages Google can index.", metric: "Dedicated SEO landers: 0 of 6 planned" },
        gap: "Home page alone cannot compete with publisher catalogues and Atom hubs.",
        actions: [
          a("practice-papers", "Ship /11-plus-practice-papers (see Keyword Rankings)", "H1 exact phrase; GL/CEM sections; free-test CTA.", "1 day"),
          a("free-test", "Ship /free-11-plus-test as an indexable page", "HTML sample questions + sign-up CTA.", "1 day"),
          a("mocks", "Ship an online mock exams page", "Timed, auto-marked, screenshots of results."),
          a("tuition", "Ship an online tuition / batches page", "Schedule, tutors, price, booking CTA."),
          a("app", "Ship an app landing page with store badges", "iOS, Android, web; free trial."),
          a("kent", "Ship the first regional guide (Kent Test)", "Template for Bucks, Essex, Birmingham next."),
        ],
      },
      {
        id: "content-hubs",
        name: "Content hubs & parent guides",
        status: "missing",
        leader: { name: "Atom Learning", note: "721 blog posts; Competitors SEO todo: 2 parent guides a week." },
        us: { note: "0 blog posts; subject guides only exist as Keyword Rankings actions.", metric: "Blog posts: 0" },
        gap: "No publish cadence or hub for informational queries (VR, NVR, GL, CEM).",
        actions: [
          a("blog-ia", "Create /blog (or /guides) with category hubs", "Subjects, exam boards, regions, parenting.", "1 day"),
          a("cadence", "Publish 2 parent guides a week for 8 weeks", "Reuse Keyword Rankings guide briefs; cite official sources.", "ongoing"),
          a("internal-links", "From every guide, link to free test + practice hubs", "Exact-match anchors where natural."),
          a("update-policy", "Add 'last updated' and yearly refresh owners", "Critical for exam-date and CEM-change articles."),
        ],
      },
    ],
  },
  {
    id: "backlinks",
    label: PILLARS.backlinks.label,
    color: PILLARS.backlinks.color,
    summary: "Link-building plan vs Atom / EPP / Eleven Plus Exams: measure the gap, then earn links via resources, outreach, directories and partnerships.",
    topics: [
      {
        id: "baseline",
        name: "Backlink baseline vs leaders",
        status: "missing",
        leader: { name: "Atom Learning / Eleven Plus Exams", note: "Strong referring-domain graphs from schools, forums and long-running resource pages (Competitors SEO evidence)." },
        us: { note: "No shared Ahrefs/Moz/Semrush snapshot in admin yet.", metric: "Referring domains: unknown" },
        gap: "Cannot prioritise outreach without a measured gap.",
        actions: [
          a("tool-access", "Connect an Ahrefs, Moz or Semrush workspace for the brand", "Read-only share for marketing admins.", "half day"),
          a("snapshot", "Record referring domains, DR/DA and top linked pages for us vs Atom vs EPP", "Paste the table into a comment; date-stamp it.", "half day"),
          a("toxic-review", "Flag any spammy links to disavow later", "Do not disavow without a second review."),
          a("cadence", "Re-check the backlink snapshot every quarter", "Update the us / leader notes on this card."),
        ],
      },
      {
        id: "resource-links",
        name: "Linkable free resources",
        status: "weak",
        leader: { name: "Free paper sites + CGP / EPE", note: "Free 11+ paper roundups dominate 'free papers' SERPs; Atom free-resource pages attract links." },
        us: { note: "Free test lives in the app; Keyword Rankings already plans PDF samples and roundup outreach.", metric: "Public linkable PDFs: 0" },
        gap: "Nothing indexable for roundup editors to link to.",
        actions: [
          a("pdf-pack", "Publish one free PDF sample per subject with answers", "Maths, English, VR, NVR on the free-test page.", "1 day"),
          a("roundups", "Pitch 10 'free 11 plus resources' list posts", "Personalised email; track replies in comments.", "1 day"),
          a("vocab-list", "Publish a yearly 11+ vocabulary list as a link magnet", "Also listed under English practice keyword."),
          a("embed", "Offer an embeddable 'question of the week' widget for blogs", "Optional; only after the free-test page exists."),
        ],
      },
      {
        id: "outreach",
        name: "Parent-blog & PTA outreach",
        status: "missing",
        leader: { name: "Atom Learning", note: "Mum-blogger discount codes (Emma and 3, Mamma Prada, Mums in the Wood) per Competitors referrals channel." },
        us: { note: "Competitors roadmap: send 10 UK parenting bloggers a free year + unique code — not started as a tracked SEO task set.", metric: "Active blogger links: 0" },
        gap: "No earned media or resource-list links from parent blogs / PTAs.",
        actions: [
          a("shortlist", "Shortlist 20 UK parenting / 11+ blogs and school PTAs", "Domain, audience, recent 11+ posts; store in a comment CSV."),
          a("free-year", "Send 10 bloggers a free year + unique code", "Matches Competitors referrals todo; ask for a resources-page link.", "2 days"),
          a("pta-pack", "Build a PTA 'free mock for members' one-pager", "PDF they can share; include UTM link."),
          a("follow-up", "Follow up non-replies at day 7 and day 21", "Log status per prospect in comments."),
        ],
      },
      {
        id: "guest-posts",
        name: "Guest posts & expert quotes",
        status: "missing",
        leader: { name: "Tutor sites / education press", note: "Guest guides and expert quotes commonly earn dofollow links in EdTech." },
        us: { note: "No guest-post pipeline.", metric: "Guest posts live: 0" },
        gap: "Missing editorial links beyond owned channels.",
        actions: [
          a("pitches", "Pitch 5 guest posts (exam timelines, GL vs CEM, mock strategy)", "Target education and parenting sites with DR 20+.", "1-2 days"),
          a("haro", "Reply to 4 relevant journalist / parent-press queries a month", "Connectively / Featured — expert quotes with a bio link."),
          a("bylines", "Publish founder bylines on LinkedIn then adapt for external sites", "Cross-link back to core landers."),
        ],
      },
      {
        id: "directories",
        name: "Directories & citations",
        status: "weak",
        leader: { name: "Local tutors / Atom", note: "Google Business Profile and education directories support 'tuition' queries." },
        us: { note: "GBP set-up is a Keyword Rankings tuition action; wider citations not tracked.", metric: "GBP: not confirmed complete" },
        gap: "Thin local / education citation footprint.",
        actions: [
          a("gbp", "Complete Google Business Profile (Tutoring service, UK)", "Photos, hours, free-test link; gather 5 genuine reviews.", "half day"),
          a("edu-directories", "Submit to 8 education / EdTech directories", "Only real listings; skip paid link farms.", "1 day"),
          a("app-stores", "Ensure App Store / Play listings link to the canonical site", "Consistent '11 Plus Magic' naming."),
          a("nap", "Standardise name, address, phone / email across citations", "Match Organization schema."),
        ],
      },
      {
        id: "partnerships",
        name: "Schools, tutors & partners",
        status: "missing",
        leader: { name: "Atom / Exam Happy", note: "School trials and tutor plans create natural .sch.uk and partner links." },
        us: { note: "Competitors referrals target: 20 tutors, 5 blogger codes, 2 school trials.", metric: "Partner links: 0 tracked" },
        gap: "No systematic partner-link asks with UTM + dofollow preference.",
        actions: [
          a("tutor-kit", "Create a tutor affiliate / referral kit with link guidelines", "Preferred anchor texts; no cloaking.", "1 day"),
          a("school-trial", "Run 2 grammar-prep school or tutor-centre trials", "Ask for a resources or parents-page link when useful."),
          a("forum-signature", "Use a compliant signature on Eleven Plus Exams where rules allow", "Helpful answers first; see Forums tab."),
          a("unlinked-mentions", "Reclaim unlinked brand mentions monthly", "Search '11 Plus Magic' / EdTech-Ltd; request a link."),
        ],
      },
    ],
  },
];

export function seoFoundationTaskId(pillarId, topicId, actionId) {
  const prefix = pillarId === "backlinks" ? "seo-backlink" : "seo-origin";
  return `${prefix}-${topicId}-${actionId}`;
}

export function flattenSeoFoundationActions(sections = SECTIONS) {
  return sections.flatMap((s) => s.topics.flatMap((t) => t.actions.map((act) => ({
    ...act,
    id: seoFoundationTaskId(s.id, t.id, act.id),
    actionSlug: act.id,
    pillarId: s.id,
    topicId: t.id,
    topicName: t.name,
  }))));
}

export function flattenTopics(sections = SECTIONS) {
  return sections.flatMap((s) => s.topics.map((t) => ({ ...t, pillarId: s.id })));
}

export function foundationSummary(sections = SECTIONS) {
  const topics = flattenTopics(sections);
  const missing = topics.filter((t) => t.status === "missing").length;
  const weak = topics.filter((t) => t.status === "weak").length;
  const strong = topics.filter((t) => t.status === "strong").length;
  return {
    pillars: sections.length,
    topics: topics.length,
    missing,
    weak,
    strong,
    originTopics: sections.find((s) => s.id === "origin")?.topics.length || 0,
    backlinkTopics: sections.find((s) => s.id === "backlinks")?.topics.length || 0,
  };
}
